import axios from "axios";
import { interceptorsRequest } from "@/utils/auth";
import request from "@/utils/request.ts";
function getFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return null;

  // 尝试匹配 filename* 参数（RFC 5987）
  const filenameStarMatch = contentDisposition.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (filenameStarMatch) {
    // 解码 URL 编码的文件名
    return decodeURIComponent(filenameStarMatch[1]);
  }

  // 回退到普通 filename 参数
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (filenameMatch) {
    return filenameMatch[1];
  }

  return null;
}

/**
 * @param {string} fileName - 默认文件名（如 "example.pdf"）
 * @param {string} url - 请求地址（GET）
 * @param {object} headers - 可选的自定义请求头
 */
export function downloadFileFn({ fileName = "", url = "", headers = {}, params }) {
  const service = axios.create({
    timeout: 60000, // 请求超时时间
  });
  service.interceptors.request.use(interceptorsRequest, (error) => {
    console.error(error);
    return Promise.reject(new Error(error));
  });
  return service({
    method: "POST",
    url: url,
    data: params,
    responseType: "blob", // 关键：指定响应类型为Blob
    headers: {
      // 默认headers（可选）
      "Cache-Control": "no-cache",
      ...headers, // 合并自定义headers
    },
  }).then((response: any) => {
    const contentDisposition = response.headers.get("Content-Disposition");
    let finalFileName = "";
    if (fileName === "") {
      if (contentDisposition) {
        finalFileName = getFilenameFromContentDisposition(contentDisposition);
      } else {
        finalFileName = fileName;
      }
    } else {
      finalFileName = fileName;
    }
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = finalFileName; // 设置下载属性
    document.body.appendChild(a);
    a.click();
    // 3. 清理资源
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  });
}

// sku时间转换
export function changeToDate(seconds, decimalPlaces = 0) {
  const hours = seconds / 3600; // 转换为小时
  const days = hours / 24; // 转换为天
  if (hours < 24) {
    // 不足24小时，显示小时
    return `${hours.toFixed(decimalPlaces)}小时`;
  } else {
    // 24小时及以上，显示天数
    return `${days.toFixed(decimalPlaces)}天`;
  }
}

/**
 * 获取数据字典
 * @param dictType
 */
// 数据字典就绪 Promise：首次 getAllDictData 完成后 resolve。
// 业务页面可通过 dictReady() 等待字典写入 localStorage 后再读取，避免首屏读到空字典。
let _dictResolve: (() => void) | null = null;
let _dictPromise = new Promise<void>((resolve) => {
  _dictResolve = resolve;
});

export function dictReady(timeout = 8000) {
  return Promise.race([_dictPromise, new Promise<void>((resolve) => setTimeout(() => resolve(), timeout))]);
}

export function getAllDictData() {
  return request
    .post("/system/sysDictType/findByCategory", {
      category: "Web",
    })
    .then((res: Record<string, any>) => {
      if (res.code == 200) {
        const dataList = (res?.data || []).map((ele) => {
          if (ele.dictDataList?.length) {
            ele.dictDatas = ele.dictDataList.map((item) => {
              return {
                label: item.dictLabel,
                value: item.dictValue,
                remark: item.remark,
                isDefault: item.isDefault,
                id: item.id,
              };
            });
          } else {
            ele.dictDatas = [];
          }
          delete ele.dictDataList;
          return ele;
        });
        localStorage.setItem("dictionary", JSON.stringify(dataList));
        localStorage.setItem("dictionaryFreshTime", JSON.stringify(Date.now()));
        // 新字典已写入 localStorage，必须同时清掉内存缓存，否则切租户/角色后
        // getDictData 仍返回旧租户的字典（内存缓存只有 1 小时过期或整页刷新才会重置）。
        _cachedAllDicts = null;
      } else {
        localStorage.setItem("dictionary", "");
      }
      return res;
    })
    .finally(() => {
      // 无论成功失败，都标记字典加载流程结束，解除等待方阻塞
      if (_dictResolve) {
        const resolve = _dictResolve;
        _dictResolve = null;
        resolve();
      }
    });
}

/**
 * 获取数据字典
 * @param dictType
 */

// 内存缓存，避免每次调用都 JSON.parse
let _cachedAllDicts = null;

export function getDictData(dictType) {
  const prevFreshTime = JSON.parse(localStorage.getItem("dictionaryFreshTime") || "0");
  if (Date.now() - prevFreshTime > 1000 * 60 * 60) {
    getAllDictData().then(() => {
      // 刷新后清除内存缓存，下次调用时重新读取
      _cachedAllDicts = null;
    });
  }
  // 优先使用内存缓存
  if (!_cachedAllDicts) {
    try {
      _cachedAllDicts = JSON.parse(localStorage.getItem("dictionary") || "[]");
    } catch (e) {
      _cachedAllDicts = [];
    }
  }
  const dictData = _cachedAllDicts.find((ele) => ele.dictType == dictType);
  if (dictType == "SKU_SECONDS" || dictType == "CLOUD_MACHINES_OPEN_NUM") {
    return (dictData?.dictDatas || []).map((ele) => {
      ele.value = Number(ele.value);
      return ele;
    });
  }
  return dictData?.dictDatas || [];
}

/**
 * 获取对应的值
 * @param dictType
 * @param value
 * @returns
 */
export function getDictLabel(dictType, value) {
  const dataDicts = getDictData(dictType);
  const currentDict = dataDicts.find((ele) => ele.value == value);
  return currentDict?.label || value;
}

/**
 * 将s格式化
 * @param seconds
 * @returns
 */
export function secondsToFormatted(seconds) {
  seconds = Number(seconds);

  const days = Math.floor(seconds / (3600 * 24));
  const hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let parts = [];
  if (days > 0) parts.push(`${days}天`);
  if (hours > 0) parts.push(`${hours}小时`);
  if (minutes > 0) parts.push(`${minutes}分钟`);
  if (remainingSeconds > 0 || parts.length === 0) parts.push(`${remainingSeconds}秒`);

  return parts.join(" ");
}

/**
 * 将s格式化为小时和分钟
 * @param seconds
 * @returns
 */
export function durationToFormatted(seconds) {
  const hours = Math.floor(seconds / 3600); // 1小时 = 3600秒
  const minutes = Math.floor((seconds % 3600) / 60); // 剩余秒数转分钟

  // 返回格式化字符串（例如：2小时30分钟）
  return `${hours}小时${minutes}分钟`;
}

/**
 * 人员分组
 * @param userData
 * @returns
 */
export function transformToTreeData(userData) {
  // 创建部门映射
  const deptMap = {};
  const newData = (userData || []).filter((ele: any) => ele.dept);
  // 遍历用户数据
  newData.forEach((user) => {
    const deptId = user.deptId;
    const deptName = user.dept.deptName;
    const deptEnName = user.dept.enUs;

    // 如果部门不存在于映射中，则创建
    if (!deptMap[deptId]) {
      deptMap[deptId] = {
        id: deptId,
        deptName: deptName,
        enUs: deptEnName,
        children: [],
      };
    }

    // 添加用户到对应部门
    deptMap[deptId].children.push({
      id: user.id,
      userName: user.userName,
      nickName: user.nickName,
      email: user.email,
      phoneNumber: user.phoneNumber,
      status: user.status,
      avatar: user.avatar,
      loginDate: user.loginDate,
    });
  });

  // 将映射转换为数组
  const filterParams = getDictData("OPERATOR_DEPT");
  if (filterParams.length) {
    return Object.values(deptMap).filter((ele: any) => filterParams.some((item) => item.value == ele.deptName));
  } else {
    return Object.values(deptMap);
  }
}

export function formatDate(date) {
  const year = date.getFullYear();
  // 月份和日期补零（确保两位数）
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}
